import cx from 'clsx';
import React, { type ChangeEvent } from 'react';
import { type Control, type FieldError, useController } from 'react-hook-form';

import useTranslation from '@hooks/useTranslation';

import style from './style.module.scss';

interface ICommonProps {
  id?: string;
  label?: string;
  disabled?: boolean;
  type?: string;
}

interface IUncontrolledProps extends ICommonProps {
  value?: boolean;
  name?: string;
  error?: FieldError | { message: string };
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
}

interface IControlledProps extends ICommonProps {
  name: string;
  control: Control<any>;
}

const Component: React.FC<IUncontrolledProps> = ({
  id,
  value,
  name,
  onChange,
  error,
  label,
  disabled,
  type,
}) => {
  const { t } = useTranslation();

  return (
    <div className={style.switchWrapper}>
      <label className={cx(style.switch, { [style.disabled]: disabled })}>
        <input
          id={id}
          type="checkbox"
          name={name}
          className={style.checkboxInput}
          checked={value}
          onChange={onChange}
          disabled={disabled}
        />
        <div className={cx(style.line, { [style.activeLine]: value })}>
          <div className={cx(style.slider, { [style.activeSlider]: value })} />
        </div>
        <span className={style.fieldLabel}>{label}</span>
      </label>
      {type !== 'woHelperText' && <div className={style.helperText}>{error && t(error?.message)}</div>}
    </div>
  );
};

const Controlled: React.FC<IControlledProps> = ({ id, name, control, label, type = '', disabled }) => {
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({
    control,
    name,
  });

  return (
    <Component
      id={id}
      value={value}
      name={name}
      onChange={onChange}
      error={error}
      label={label}
      disabled={disabled}
      type={type}
    />
  );
};

const Switch: React.FC<IControlledProps | IUncontrolledProps> = (props) => {
  if ('control' in props) {
    return <Controlled {...props} />;
  }

  return <Component {...props} />;
};

export default Switch;
